import "@voicerun/design-system/dist/index.css";

import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Provider } from "react-redux";
import { StyleGuide } from "@voicerun/design-system";

import { store } from "./redux";
import { ThemeProvider } from "./contexts/ThemeContext";

import { AdminLayout } from "./layouts/AdminLayout";
import { AgentDetailLayout } from "./layouts/AgentDetailLayout";
import { AppLayout } from "./layouts/AppLayout";

import { AdminOrganizationsPage } from "./pages/admin/AdminOrganizationsPage";
import { AdminTrainingJobsPage } from "./pages/admin/AdminTrainingJobsPage";
import { AdminUsersPage } from "./pages/admin/AdminUsersPage";
import { AgentDetailsPage } from "./pages/agent/AgentDetailsPage";
import { AgentDeploymentsPage } from "./pages/agent/AgentDeploymentsPage";
import { AgentFunctionPage } from "./pages/agent/AgentFunctionPage";
import { AgentRecipesPage } from "./pages/agent/AgentRecipesPage";
import { AgentRecipeCreationPage } from "./pages/agent/AgentRecipeCreationPage";
import { AgentsPage } from "./pages/agent/AgentsPage";
import { ForgotPasswordPage } from "./pages/ForgotPassword";
import { VoiceLibraryPage } from "./pages/VoiceLibraryPage";
import { InviteAcceptPage } from "./pages/InviteAcceptPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { OrganizationPage } from "./pages/OrganizationPage";
import { ProfilePage } from "./pages/ProfilePage";
import { SignInPage } from "./pages/SignInPage";
import { SignOutPage } from "./pages/SignOutPage";
import { SignUpPage } from "./pages/SignUpPage";
import { AgentEnvironmentsPage } from "./pages/agent/AgentEnvironmentsPage";
import { AgentRecipeDetailsPage } from "./pages/agent/AgentRecipeDetailsPage";
import { AgentSessionsPage } from "./pages/agent/AgentSessionsPage";
import { IndexPage } from "./pages/IndexPage";
import { StandardLayout } from "./layouts/StandardLayout";
import { CliLoginPage } from "./pages/CliLoginPage";
import { AgentTestingPage } from "./pages/agent/AgentTestingPage";
import { AgentEvaluationPage } from "./pages/agent/AgentEvaluationPage";
import { AgentEvaluationAssignmentsTab } from "./pages/agent/AgentEvaluationAssignmentsTab";
import { AgentEvaluationEvaluateTab } from "./pages/agent/AgentEvaluationEvaluateTab";
import { AgentEvaluationEvaluationsTab } from "./pages/agent/AgentEvaluationEvaluationsTab";
import { AgentSimulationsPage } from "./pages/agent/AgentSimulationsPage";
import { NewSimulationAgentPage } from "./pages/agent/NewSimulationAgentPage";
import { SimulationAgentDetailsPage } from "./pages/agent/SimulationAgentDetailsPage";
import { EvaluatorsPage } from "./pages/evaluator/EvaluatorsPage";
import { NewEvaluatorPage } from "./pages/evaluator/NewEvaluatorPage";
import { EvaluatorDetailsPage } from "./pages/evaluator/EvaluatorDetailsPage";
import { ResourcesPage } from "./pages/ResourcesPage";
import { RequireAuth } from "./components/auth/RequireAuth";
import { NotificationsPage } from "./pages/NotificationsPage";
import { NotificationPreferencesPage } from "./pages/NotificationPreferencesPage";

export const App = () => {
  return (
    <Provider store={store}>
      <ThemeProvider>
        <BrowserRouter>
          <Routes>
            <Route
              path="/"
              element={<AppLayout />}
            >
              <Route
                path="/signin"
                element={<SignInPage />}
              />
              <Route
                path="/signup"
                element={<SignUpPage />}
              />
              <Route
                path="/signout"
                element={<SignOutPage />}
              />
              <Route
                path="/style-guide"
                element={<StyleGuide />}
              />
              <Route
                path="/cli-login"
                element={<CliLoginPage />}
              />
              <Route
                path="/forgot-password"
                element={<ForgotPasswordPage />}
              />
              <Route
                path="/invites/:inviteId/accept"
                element={<InviteAcceptPage />}
              />
              <Route
                path="/"
                element={<StandardLayout />}
              >
                <Route
                  path="/"
                  element={<IndexPage />}
                />
                <Route
                  path="/profile"
                  element={<ProfilePage />}
                />
                <Route
                  path="/notifications"
                  element={<NotificationsPage />}
                />
                <Route
                  path="/notification-preferences"
                  element={<NotificationPreferencesPage />}
                />
                <Route
                  path="/organization"
                  element={<OrganizationPage />}
                />
                <Route
                  path="/resources"
                  element={<ResourcesPage />}
                />
                <Route
                  path="/voice-library"
                  element={<VoiceLibraryPage />}
                />
                <Route
                  path="/agents"
                  element={<AgentsPage />}
                />
                <Route
                  path="/agents/new"
                  element={<AgentFunctionPage />}
                />
                <Route
                  path="/evaluators"
                  element={<EvaluatorsPage />}
                />
                <Route
                  path="/evaluators/new"
                  element={<NewEvaluatorPage />}
                />
                <Route
                  path="/evaluators/:evaluatorId/details"
                  element={<EvaluatorDetailsPage />}
                />
                <Route
                  path="/agents/simulations"
                  element={<AgentSimulationsPage />}
                />
                <Route
                  path="/agents/simulations/new"
                  element={<NewSimulationAgentPage />}
                />
                <Route
                  path="/agents/simulations/:simulationAgentId"
                  element={<SimulationAgentDetailsPage />}
                />
                <Route
                  path="/agents/recipes"
                  element={<AgentRecipesPage />}
                />

                <Route
                  path="/agents/recipes/:agentRecipeId"
                  element={<AgentDetailLayout />}
                >
                  <Route
                    index
                    element={<AgentRecipeDetailsPage />}
                  />
                </Route>

                <Route
                  path="/agents/:agentId"
                  element={<AgentDetailLayout />}
                >
                  <Route
                    index
                    element={
                      <Navigate
                        to="overview"
                        replace
                      />
                    }
                  />
                  <Route
                    path="overview"
                    element={<AgentDetailsPage />}
                  />
                  <Route
                    path="environments"
                    element={<AgentEnvironmentsPage />}
                  />
                  <Route
                    path="function"
                    element={<AgentFunctionPage />}
                  />
                  <Route
                    path="create-recipe"
                    element={<AgentRecipeCreationPage />}
                  />
                  <Route
                    path="sessions"
                    element={<AgentSessionsPage />}
                  />
                  <Route
                    path="deployments"
                    element={<AgentDeploymentsPage />}
                  />
                  <Route
                    path="evaluation"
                    element={<AgentEvaluationPage />}
                  >
                    <Route
                      index
                      element={
                        <Navigate
                          to="evaluations"
                          replace
                        />
                      }
                    />
                    <Route
                      path="evaluations"
                      element={<AgentEvaluationEvaluationsTab />}
                    />
                    <Route
                      path="assignments"
                      element={<AgentEvaluationAssignmentsTab />}
                    />
                    <Route
                      path="evaluate"
                      element={<AgentEvaluationEvaluateTab />}
                    />
                  </Route>
                  <Route
                    path="testing"
                    element={<AgentTestingPage />}
                  />
                </Route>
              </Route>

              <Route
                path="/admin"
                element={<AdminLayout />}
              >
                <Route
                  path="/admin"
                  element={<Navigate to="/admin/users" />}
                />
                <Route
                  path="/admin/users"
                  element={<AdminUsersPage />}
                />
                <Route
                  path="/admin/organizations"
                  element={<AdminOrganizationsPage />}
                />
                <Route
                  path="/admin/trainingJobs"
                  element={<AdminTrainingJobsPage />}
                />
              </Route>
              <Route
                path="*"
                element={<NotFoundPage />}
              />
            </Route>
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    </Provider>
  );
};
